import { useMutation, useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'

import { gql } from '@/clients/utoniq-core'
import {
  CancelTicketDistributionMutation,
  CancelTicketDistributionMutationVariables,
  RegenerateTicketDistributionLinkMutation,
  RegenerateTicketDistributionLinkMutationVariables,
  TicketShareCopyQuery,
  TicketShareCopyQueryVariables,
  TicketShareCopyUserItemSerialFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { useInitialLoading } from '@/components/lib/initialLoading'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('ticketShareCopy')
  const { showError, showMessage } = useMessage()
  const { router } = useRouter<{
    itemId: string
    serialId: string
    throughTicketIndex?: string
    isFirstGenerate?: 'true'
  }>()
  const [regenerated, setRegenerated] = useState<boolean>(false)

  const { data, loading } = useQuery<
    TicketShareCopyQuery,
    TicketShareCopyQueryVariables
  >(QUERY, {
    variables: { id: router.query.itemId },
  })
  const [cancelTicketDistribution, { loading: canceling }] = useMutation<
    CancelTicketDistributionMutation,
    CancelTicketDistributionMutationVariables
  >(CANCEL_TICKET_DISTRIBUTION)

  const [regenerateTicketDistributionLink, { loading: regenerating }] =
    useMutation<
      RegenerateTicketDistributionLinkMutation,
      RegenerateTicketDistributionLinkMutationVariables
    >(REGENERATE_TICKET_DISTRIBUTION_LINK)

  const serial = useMemo(
    () =>
      data?.portfolio?.serials?.find(
        serial => serial?.serial?.id === router.query.serialId
      ) ?? null,
    [data?.portfolio, router.query.serialId]
  )

  return {
    state: {
      loading: useInitialLoading(data, loading),
      canceling,
      regenerating,
      serial,
      isFirstGenerate: router.query.isFirstGenerate === 'true',
      regenerated,
      throughTicketIndex: data?.portfolio?.item.metadata?.ticket
        ?.isThroughTicket
        ? router.query.throughTicketIndex
        : undefined,
      artistId: data?.portfolio?.item?.issuedArtist?.id ?? '',
    },
    actions: {
      async onRegenerateTicketDistribution() {
        await regenerateTicketDistributionLink({
          variables: {
            inputs: {
              token: serial?.serial?.ticketDistributionLink?.token ?? '',
            },
          },
        })
          .then(() => {
            showMessage(t('regenerateURLSuccess'), 'success')
            setRegenerated(true)
          })
          .catch(showError)
      },
      async onCancelTicketDistribution() {
        await cancelTicketDistribution({
          variables: {
            inputs: {
              token: serial?.serial?.ticketDistributionLink?.token ?? '',
            },
          },
        })
          .then(() => showMessage(t('canceledDistributionSuccess'), 'success'))
          .catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query TicketShareCopy($id: ID!) {
    portfolio(id: $id) {
      serials {
        ...TicketShareCopyUserItemSerial
      }
      item {
        issuedArtist {
          id
        }
        metadata {
          ticket {
            isThroughTicket
          }
        }
      }
    }
  }
  ${TicketShareCopyUserItemSerialFragmentDoc}
`

const REGENERATE_TICKET_DISTRIBUTION_LINK = gql`
  mutation regenerateTicketDistributionLink(
    $inputs: RegenerateTicketDistributionLinkInput!
  ) {
    regenerateTicketDistributionLink(inputs: $inputs) {
      id
      distributionId
      expiredAt
      formattedExpiredAt @client
      token
    }
  }
`

const CANCEL_TICKET_DISTRIBUTION = gql`
  mutation cancelTicketDistribution($inputs: CancelTicketDistributionInput!) {
    cancelTicketDistribution(inputs: $inputs) {
      id
      distributionId
      expiredAt
      formattedExpiredAt @client
      token
      serialId
    }
  }
`
